import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';

// assets
import BellOutlined from '@ant-design/icons/BellOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';

import { getComplaint } from '../../../../api/adminApi';

// sx styles
const avatarSX = {
  width: 36,
  height: 36,
  fontSize: '1rem'
};

const actionSX = {
  mt: '6px',
  ml: 1,
  top: 'auto',
  right: 'auto',
  alignSelf: 'flex-start',

  transform: 'none'
};

const getComplaintInstant = (c) => {
  const raw = c?.createdAt ?? c?.date;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

const isTodayOrYesterday = (d) => {
  if (!d) return false;
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const startOfTomorrow = new Date(startOfToday);
  startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
  return d >= startOfYesterday && d < startOfTomorrow;
};

const isClosedComplaint = (c) => {
  const status = String(c?.status ?? '').toLowerCase().trim();
  return status === 'close' || status === 'closed';
};

const dayLabel = (d) => {
  if (!d) return '';
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const t = d.getTime();
  if (t >= startOfToday.getTime()) return 'Today';
  if (t >= startOfYesterday.getTime() && t < startOfToday.getTime()) return 'Yesterday';
  return '';
};

const complaintKey = (c) => String(c?.ticket_id ?? c?._id ?? c?.id ?? '');

const formatCreatedLine = (c, at) => {
  const datePart =
    at != null
      ? at.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      : '—';
  const timePart =
    c?.time && String(c.time).trim()
      ? String(c.time).trim()
      : at != null
        ? at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
        : '—';
  return { datePart, timePart };
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

export default function Notification() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [complaints, setComplaints] = useState([]);
  const [readIds, setReadIds] = useState(() => new Set());

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await getComplaint(1, 500);
        const data = Array.isArray(res?.data) ? res.data : [];
        if (alive) setComplaints(data);
      } catch {
        if (alive) setComplaints([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const recentComplaints = useMemo(() => {
    const withInstant = complaints
      .map((c) => ({ c, at: getComplaintInstant(c) }))
      .filter(({ at, c }) => at && isTodayOrYesterday(at) && !isClosedComplaint(c))
      .sort((a, b) => b.at.getTime() - a.at.getTime());
    return withInstant;
  }, [complaints]);

  const unreadCount = useMemo(() => {
    return recentComplaints.filter(({ c }) => {
      const k = complaintKey(c);
      return k && !readIds.has(k);
    }).length;
  }, [recentComplaints, readIds]);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const markAllRead = () => {
    setReadIds((prev) => {
      const next = new Set(prev);
      recentComplaints.forEach(({ c }) => {
        const k = complaintKey(c);
        if (k) next.add(k);
      });
      return next;
    });
  };

  const markOneRead = (c) => {
    const k = complaintKey(c);
    if (!k) return;
    setReadIds((prev) => new Set(prev).add(k));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={(theme) => ({
          color: 'text.primary',
          bgcolor: open ? 'grey.100' : 'transparent',
        })}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={unreadCount > 0 ? unreadCount : 0} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: '100%', minWidth: 285, maxWidth: { xs: 285, md: 420 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Complaint notifications"
                  elevation={0}
                  border={false}
                  content={false}
                  secondary={
                    <>
                      {unreadCount > 0 && (
                        <Tooltip title="Mark all as read">
                          <IconButton color="success" size="small" onClick={markAllRead}>
                            <CheckCircleOutlined style={{ fontSize: '1.15rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  }
                >
                  <List
                    component="nav"
                    sx={{
                      p: 0,
                      '& .MuiListItemButton-root': {
                        py: 0.5,
                        px: 2,
                        '&.Mui-selected': { bgcolor: 'grey.50', color: 'text.primary' },
                        '& .MuiAvatar-root': avatarSX,
                        '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
                      }
                    }}
                  >
                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={28} />
                      </Box>
                    ) : recentComplaints.length === 0 ? (
                      <ListItem>
                        <ListItemText
                          primary={
                            <Typography variant="body2" color="text.secondary">
                              No complaints from today or yesterday.
                            </Typography>
                          }
                        />
                      </ListItem>
                    ) : (
                      recentComplaints.map(({ c, at }, index) => {
                        const key = complaintKey(c) || `row-${index}`;
                        const k = complaintKey(c);
                        const isUnread = k && !readIds.has(k);
                        const { datePart, timePart } = formatCreatedLine(c, at);
                        const label = dayLabel(at);
                        const secondaryTime =
                          at != null
                            ? at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                            : '—';

                        return (
                          <ListItem
                            key={key}
                            component={ListItemButton}
                            divider
                            selected={isUnread}
                            onClick={() => {
                              markOneRead(c);
                              const id = c._id || c.id;
                              if (id) {
                                navigate(`/complaints/${id}`, { state: { complaint: c } });
                                setOpen(false);
                              }
                            }}
                            secondaryAction={
                              <Typography variant="caption" noWrap>
                                {secondaryTime}
                              </Typography>
                            }
                          >
                            <ListItemAvatar>
                              <Avatar sx={{ color: 'primary.main', bgcolor: 'primary.lighter' }}>
                                <MessageOutlined />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600 }}>
                                  {c.issue_type || c.issue_category || 'Complaint'}
                                </Typography>
                              }
                              secondary={
                                <Stack component="span" spacing={0.25} sx={{ mt: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary" component="span" display="block">
                                    Ticket ID: {c.ticket_id ?? '—'} · User ID: {c.user_id ?? '—'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" component="span" display="block">
                                    {label} · {datePart} · {timePart}
                                  </Typography>
                                  {c.createdAt && (
                                    <Typography variant="caption" color="text.disabled" component="span" display="block">
                                      createdAt: {new Date(c.createdAt).toLocaleString('en-GB')}
                                    </Typography>
                                  )}
                                </Stack>
                              }
                            />
                          </ListItem>
                        );
                      })
                    )}
                    <ListItemButton
                      sx={{ textAlign: 'center', py: `${12}px !important` }}
                      onClick={() => {
                        navigate('/complaints/list');
                        setOpen(false);
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="h6" color="primary">
                            View all
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
